import { APIScope, FeatureFlagKey } from "@swivl-mono/types";
import {
  DropdownContainer,
  DropdownOption,
  DropdownPosition,
  HeaderTabsComponent,
  LayoutWithWideSidebar,
  SearchInput,
  Switch,
  SwitchVariant
} from "@swivl-mono/ui";
import React, { useCallback, useEffect } from "react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { HiOutlineSearch } from "react-icons/hi";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";

import { isFeatureEnabled } from "../../Helpers/FeatureFlags/isFeatureEnabled";
import { isSequencesConfigPageVisible } from "../../Helpers/FeatureFlags/isSequencesConfigPageVisible";
import { useHasPermission } from "../../Helpers/Permissions/useHasPermission";
import { usePropertiesGetLocationIds } from "../../Hooks/usePropertiesGetLocationIds";
import { useViewCustomizations } from "../../Hooks/useViewCustomizations";
import LocationsModel from "../../Models/Locations/Locations.Model";
import ModalModel from "../../Models/Modal/Modal.Model";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import GlobalSearchModalModel from "../../Search/GlobalSearchModal.Model";
import "../../Worksheets/Worksheets.scss";
import { LocationsListComponent } from "./Components/Locations.List.Component";
import { AddLocationV3Modal } from "./Modals/AddLocationV3.Modal";
import { AddLocationOrGroupModal } from "./Modals/AddLocationsOrGroup.Modal";
import { PropertiesViewPage } from "./Properties.View";

interface PropertiesNavContentProps {
  items: { to: string; label: string }[];
  legacyHidden: boolean;
}

function PropertiesV3NavContent(props: PropertiesNavContentProps): React.ReactNode {
  return (
    <div className="flex w-full items-center gap-3">
      <span className="mr-8 font-poppins text-lg font-semibold">{props.legacyHidden ? "Property Manager" : "Property Manager - NEW"}</span>
      <HeaderTabsComponent items={props.items} />
      <button
        type="button"
        onClick={() => GlobalSearchModalModel.open()}
        aria-label="Search Property Manager"
        className="ml-auto flex size-[34px] shrink-0 cursor-pointer items-center justify-center text-grey-600 hover:text-orange-500"
      >
        <HiOutlineSearch className="size-4" />
      </button>
    </div>
  );
}

interface SidebarHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onAddLocation: () => void;
  onAddGroup: () => void;
}

function SidebarHeader(props: SidebarHeaderProps): React.ReactNode {
  const { searchQuery, onSearchChange, onAddLocation, onAddGroup } = props;
  const [showMenu, setShowMenu] = React.useState(false);
  const [viewCustomizations, toggleViewCustomizations] = useViewCustomizations();
  const canCreate = useHasPermission(APIScope.LOCATION_CREATE);

  const handleClear = (): void => {
    onSearchChange("");
  };

  const handleSelect = (value: string): void => {
    if (value === "add-location") {
      onAddLocation();
    } else if (value === "add-group") {
      onAddGroup();
    }
  };

  return (
    <div className="flex w-full items-center gap-2 border-b border-grey-200 p-3">
      <SearchInput placeholder="Search..." value={searchQuery} onChange={(e) => onSearchChange(e.target.value)} onClear={handleClear} />
      <DropdownContainer
        isOpen={showMenu}
        onOpenChange={setShowMenu}
        position={DropdownPosition.RIGHT}
        trigger={
          <button className="flex cursor-pointer items-center justify-center text-grey-600 hover:text-grey-800" aria-label="More options">
            <BiDotsVerticalRounded size={18} />
          </button>
        }
      >
        <div className="flex w-full items-center justify-between gap-3 p-2">
          <span className="body-sm">Customization Count</span>
          <Switch on={viewCustomizations} onSet={toggleViewCustomizations} variant={SwitchVariant.TOGGLE} />
        </div>
        {canCreate && <div className="my-1 h-px w-full bg-grey-200" />}
        {canCreate && <DropdownOption option={{ label: "Onboard Locations", value: "add-location" }} onSelect={handleSelect} />}
        {canCreate && <DropdownOption option={{ label: "Add Group", value: "add-group" }} onSelect={handleSelect} />}
      </DropdownContainer>
    </div>
  );
}

/**
 * Property Manager v3 — same shell as the legacy Property Manager (locations
 * sidebar, header tabs, NavBar wiring) but with only the v3 surfaces:
 * Details v3 (facts & typed settings) + Policies (structured worksheets).
 *
 * Long-term, this replaces `PropertiesView` once v3 reaches parity. For now
 * both live side-by-side and the user picks via the main left nav.
 */
export function PropertiesV3View(): React.ReactElement {
  const [searchQuery, setSearchQuery] = React.useState("");

  const page = usePropertiesGetLocationIds();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Permission AND the sequences_page flag + engine switches (ENG-3542).
  const canViewSequences = useHasPermission(APIScope.SEQUENCES_VIEW, page.groupId, page.locationId) && isSequencesConfigPageVisible();

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  // Deep link from the onboarding ticket: ?onboard=<locationId>&source=<sourceId> pops the wizard.
  useEffect(() => {
    const onboard = searchParams.get("onboard");
    if (!onboard) return;
    const source = searchParams.get("source");
    ModalModel.showModalWithContent(
      <h2>Onboard Locations</h2>,
      <AddLocationV3Modal navigate={navigate} resumeLocationId={Number(onboard)} resumeSourceId={source != null ? Number(source) : null} />
    );
    searchParams.delete("onboard");
    searchParams.delete("source");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, navigate, setSearchParams]);

  const handleAddLocation = useCallback((): void => {
    ModalModel.showModalWithContent(<h2>Onboard Locations</h2>, <AddLocationV3Modal navigate={navigate} />);
  }, [navigate]);

  const handleAddGroup = useCallback((): void => {
    ModalModel.showModalWithContent(<h2>Add Group</h2>, <AddLocationOrGroupModal type="group" navigate={navigate} />);
  }, [navigate]);

  const headerTabItems = [
    { to: `${page.preamble}/page/${PropertiesViewPage.DETAILS_V3}`, label: "Details" },
    ...(isFeatureEnabled(FeatureFlagKey.PolicyBuilder)
      ? [{ to: `${page.preamble}/page/${PropertiesViewPage.POLICY_WORKSHEETS}`, label: "Policies" }]
      : []),
    // The v2 spot.is page, duplicated onto the Cortex spot variables the bot reads (Chuck, 2026-08-30).
    { to: `${page.preamble}/page/${PropertiesViewPage.SPOT}`, label: "spot.is" },
    // The new sequence engine's config editor (ENG-3216) — org / group / location.
    ...(canViewSequences ? [{ to: `${page.preamble}/page/${PropertiesViewPage.SEQUENCES}`, label: "Sequences" }] : []),
    { to: `${page.preamble}/page/${PropertiesViewPage.REPORTS}`, label: "Reports" }
  ];

  const legacyHidden = isFeatureEnabled(FeatureFlagKey.HideLegacyNav) && isFeatureEnabled(FeatureFlagKey.Snowy);
  const title = legacyHidden ? "Property Manager" : "Property Manager - NEW";

  useEffect(() => {
    NavBar.set(<PropertiesV3NavContent items={headerTabItems} legacyHidden={legacyHidden} />, null, null, title);
  }, [page.preamble, page.type, handleAddLocation, handleAddGroup, legacyHidden, title, canViewSequences]);

  return (
    <LayoutWithWideSidebar
      sidebarClassName="!overflow-visible"
      sidebar={
        <div className="flex h-full flex-col">
          <div className="sticky top-0 z-60 w-full overflow-visible bg-white">
            <SidebarHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} onAddLocation={handleAddLocation} onAddGroup={handleAddGroup} />
          </div>
          <div className="flex-1 overflow-y-auto pb-10">
            <LocationsListComponent searchQuery={searchQuery} varSet="cortex" {...page} />
          </div>
        </div>
      }
      contentNoPadding={true}
      content={
        <div className="LocationView__content relative h-full">
          <Outlet />
        </div>
      }
    />
  );
}
